/**
 * The page heading plus empty-state anatomy. The empty-state mark is
 * deferred because the repo ships no icon set. An optional back link renders
 * first, above the heading, per the Back affordance section of ui-patterns.md.
 */
import { Button } from "@sportsmet/ui";
import type { ReactNode } from "react";

/** One optional button under an empty or failed section, such as Try again. */
export type typeSectionEmptyStateAction = {
  label: string;
  onAction: () => void;
};

export const SectionEmptyState = ({
  headline,
  body,
  action,
}: {
  headline: string;
  body?: string | undefined;
  action?: typeSectionEmptyStateAction | undefined;
}): ReactNode => (
  <div className="mx-auto flex max-w-120 flex-col items-center gap-2 py-10 text-center">
    <p className="text-body-strong text-ink">{headline}</p>
    {body !== undefined ? (
      <p className="text-body text-ink-secondary">{body}</p>
    ) : null}
    {action !== undefined ? (
      <Button variant="secondary" className="mt-2" onClick={action.onAction}>
        {action.label}
      </Button>
    ) : null}
  </div>
);

export const SectionScreen = ({
  heading,
  back,
  children,
}: {
  /** The section's h1. Null for a detail screen that draws its own h1 in
   *  its top row (a game, a team, standings), so the page never stacks the
   *  section name over the screen's own title. */
  heading: string | null;
  back?: ReactNode | undefined;
  children: ReactNode;
}): ReactNode => (
  <div className="flex flex-col gap-4">
    {back}
    {heading !== null ? <h1 className="text-h1 text-ink">{heading}</h1> : null}
    {children}
  </div>
);
